// Full blog articles for Naari Sutra. Content is block-based:
// { type: "p" | "h2" | "quote" | "list", text | items }
// Posts with an `href` are standalone HTML pages served from /public/blog
// (opened directly) instead of being rendered from `content` blocks.
export const BLOG_ARTICLES = [
  {
    slug: "navratri-style-guide",
    title: "Navratri Style Guide",
    category: "Festive Style",
    date: "Oct 2026",
    read: "6 min read",
    author: "LookSorted",
    excerpt:
      "Your complete LookSorted guide to dressing for all nine nights — outfits, hair, makeup and the finishing touches.",
    image: "/assets/navratri-hub.jpg",
    href: "/blog/navratri-style-guide.html",
  },
  {
    slug: "navratri-outfits",
    title: "What Should I Wear for Navratri?",
    category: "Outfits",
    date: "Oct 2026",
    read: "7 min read",
    author: "LookSorted",
    excerpt:
      "Chaniya choli, fusion or indo-western? How to pick the right Navratri outfit for every garba night.",
    image: "/assets/navratri-outfits.jpg",
    href: "/blog/navratri-outfits.html",
  },
  {
    slug: "navratri-hairstyles",
    title: "Your Perfect Navratri Hairstyle",
    category: "Hair",
    date: "Oct 2026",
    read: "5 min read",
    author: "LookSorted",
    excerpt:
      "Braids, buns and flower-adorned looks that survive hours of garba — matched to your outfit and hair type.",
    image: "/assets/navratri-hairstyles.jpg",
    href: "/blog/navratri-hairstyles.html",
  },
  {
    slug: "navratri-makeup",
    title: "Navratri Makeup That Lasts",
    category: "Makeup",
    date: "Oct 2026",
    read: "5 min read",
    author: "LookSorted",
    excerpt:
      "A sweat-proof, dance-proof makeup routine that stays put from the first dandiya to the last.",
    image: "/assets/navratri-makeup.jpg",
    href: "/blog/navratri-makeup.html",
  },
  {
    slug: "navratri-finishing-touches",
    title: "The Finishing Touches",
    category: "Accessories",
    date: "Oct 2026",
    read: "6 min read",
    author: "LookSorted",
    excerpt:
      "Jewellery, footwear, dupatta drapes, bags and bangles — the final styling checks that complete your look.",
    image: "/assets/navratri-finishing-touches.jpg",
    href: "/blog/navratri-finishing-touches.html",
  },
  {
    slug: "navratri-nine-days",
    title: "Nine Days, Nine Looks",
    category: "Outfits",
    date: "Oct 2026",
    read: "6 min read",
    author: "LookSorted",
    excerpt:
      "How to plan nine distinct Navratri looks from one small capsule wardrobe — without repeating yourself.",
    image: "/assets/navratri-nine-days.jpg",
    href: "/blog/navratri-nine-days.html",
  },
  {
    slug: "decision-fatigue",
    title: "The Hidden Cost of Decision Fatigue",
    category: "Decision Intelligence",
    date: "Jan 2026",
    read: "5 min read",
    author: "Naari Sutra Team",
    excerpt:
      "We make thousands of micro-decisions daily. Here's why that quietly drains you — and how AI can help.",
    image:
      "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    content: [
      { type: "p", text: "You wake up and the questions begin. What's for breakfast? What should the kids eat? What do I wear to that meeting? Is there anything defrosted for dinner? Did we run out of milk? By the time most people have their first coffee, they've already made dozens of small decisions — and the day has barely started." },
      { type: "p", text: "Researchers estimate that an adult makes tens of thousands of decisions a day. The overwhelming majority of them are tiny, repetitive, and low-stakes. But their sheer volume adds up to something psychologists call decision fatigue: the gradual erosion of your ability to make good choices as the number of choices you've already made increases." },
      { type: "h2", text: "Why small decisions cost so much" },
      { type: "p", text: "Decision-making draws on the same limited pool of mental energy you use for focus, patience, and self-control. Every trivial choice — which brand of pasta, what to cook, whether to reply now or later — spends a little of that reserve. The decisions themselves feel weightless, but the cumulative load is very real." },
      { type: "p", text: "This is why people tend to make worse choices as the day wears on. It's why you order takeout at 8pm even though you meant to cook, and why the supermarket at the end of a long week feels genuinely exhausting. The problem was never any single decision. The problem is the tax you pay across all of them." },
      { type: "quote", text: "Information is everywhere. Good decisions aren't." },
      { type: "h2", text: "More options is not the same as more freedom" },
      { type: "p", text: "For twenty years, technology's answer to everyday life has been more: more recipes, more products, more content, more choice. But an infinite feed of options doesn't reduce cognitive load — it multiplies it. Searching is work. Comparing is work. Deciding, in the end, is the hardest work of all." },
      { type: "p", text: "The result is a strange modern paradox. We have never had more information about what to cook, wear, buy, or use — and we have never felt more stuck. The bottleneck moved from access to attention." },
      { type: "h2", text: "What good AI should actually do" },
      { type: "p", text: "This is the shift Naari is built around. We believe AI's job is not to give you ten more options to evaluate. Its job is to understand your context — your preferences, routines, family, weather, calendar, and habits — and hand you one confident, well-reasoned recommendation you can act on immediately." },
      { type: "list", items: [
        "Fewer tabs open, fewer comparisons to make.",
        "Recommendations that already account for what you have and what you like.",
        "Decisions that arrive at the right moment, not buried in a feed.",
      ]},
      { type: "p", text: "Reducing decision fatigue isn't about doing less thinking — it's about spending your thinking where it matters. Less searching. Less overthinking. More living." },
    ],
  },
  {
    slug: "ai-that-decides",
    title: "From Search to Decide: The Next Shift in AI",
    category: "AI",
    date: "Jan 2026",
    read: "6 min read",
    author: "Naari Sutra Team",
    excerpt:
      "Search gave us information. The next generation of AI gives us decisions. Here's what changes.",
    image:
      "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    content: [
      { type: "p", text: "The last era of technology was defined by search. Type a question, get a page of links, and figure out the answer yourself. Search was revolutionary — it democratized access to information. But it left the hardest part of the job entirely to you: turning that information into a decision." },
      { type: "h2", text: "The gap between knowing and doing" },
      { type: "p", text: "Search is brilliant at knowing. It is useless at doing. It will happily return two million recipes, but it will not tell you what to cook tonight given the vegetables wilting in your fridge, the fact that it's a school night, and that one person in the house won't touch mushrooms." },
      { type: "p", text: "That gap — between a page of results and a confident next action — is exactly where most of everyday friction lives. And it's precisely the gap that a new generation of AI is built to close." },
      { type: "quote", text: "Instead of making you search, we help you decide." },
      { type: "h2", text: "What 'decision-first' AI looks like" },
      { type: "p", text: "A decision-first system starts from a different question. Not 'what information matches this query?' but 'given everything I know about this person and this moment, what is the single best thing to recommend?'" },
      { type: "p", text: "That requires context. Real recommendations depend on personal preferences, family and household needs, the weather, your calendar, your budget, and the habits you've built over time. When AI can weave those signals together, the output stops being a list and starts being an answer." },
      { type: "list", items: [
        "Search returns options. Decision AI returns a recommendation.",
        "Search is generic. Decision AI is personal and contextual.",
        "Search ends when you close the tab. Decision AI learns and improves for next time.",
      ]},
      { type: "h2", text: "Why this matters now" },
      { type: "p", text: "The technology to do this well has only recently matured. Modern models can understand nuance, reason across many signals, and adapt to feedback. For the first time, it's realistic to build products that don't just surface information but genuinely reduce the number of decisions you have to make yourself." },
      { type: "p", text: "At Naari, every product — from CookSorted to LookSorted to GlowSorted — is built on this principle. We're not trying to give you a better search box. We're trying to give you back the mental space that search never could." },
    ],
  },
  {
    slug: "everyday-os",
    title: "Why the World Needs an Operating System for Everyday Life",
    category: "Vision",
    date: "Dec 2025",
    read: "7 min read",
    author: "Ambalika Smiti",
    excerpt:
      "Apps solve single problems. Life isn't single-problem. A look at the case for a unified everyday-life OS.",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    content: [
      { type: "p", text: "There's an app for almost everything. One for meal planning. One for your wardrobe. One for skincare. One for chores. One for groceries. Each is competent at its narrow slice of your day. And yet, taken together, they don't make life feel simpler — they make it feel fragmented." },
      { type: "h2", text: "The problem with single-purpose apps" },
      { type: "p", text: "Your life doesn't happen in silos. What you cook affects what you buy. The weather affects what you wear and what you make for dinner. Your calendar affects how much time you have for any of it. But your apps don't talk to each other. Each one asks you to re-enter your preferences, re-learn its interface, and make yet another set of decisions in isolation." },
      { type: "p", text: "The burden of connecting all of these — of being the integration layer between a dozen disconnected tools — falls entirely on you. You are the operating system, and it's exhausting." },
      { type: "quote", text: "We're not building apps. We're building the operating system for everyday life." },
      { type: "h2", text: "What an everyday-life OS actually means" },
      { type: "p", text: "An operating system does something quiet but profound: it creates a shared foundation so that everything running on top of it can cooperate. It manages resources, remembers context, and lets applications work together instead of in isolation." },
      { type: "p", text: "That's the vision behind Naari. A single intelligence that understands you once — your tastes, your household, your routines, your constraints — and then powers every everyday decision on top of that shared understanding. CookSorted, LookSorted, GlowSorted, and HomeSorted aren't separate apps you manage. They're expressions of one system that already knows you." },
      { type: "list", items: [
        "Learn your context once, apply it everywhere.",
        "Decisions in one domain inform decisions in another.",
        "Less setup, less repetition, less mental overhead.",
      ]},
      { type: "h2", text: "Why now, and why us" },
      { type: "p", text: "Today's women — and increasingly entire households — make hundreds of small decisions every day. That invisible labour rarely gets named, let alone designed for. We're building AI-powered products specifically to remove it." },
      { type: "p", text: "The pieces have finally come together: AI capable of real personalization, and a clear understanding of the everyday problems worth solving. An operating system for daily life isn't a metaphor for us. It's the roadmap." },
      { type: "p", text: "Every day people ask: what should I cook, what should I wear, what should I buy, what should I clean, what should I use for my skin? We're building intelligent products that answer those questions — together, not one app at a time." },
    ],
  },
  {
    slug: "personalization-nutrition",
    title: "Personalization Done Right: Nutrition That Fits Your Week",
    category: "Nutrition",
    date: "Dec 2025",
    read: "4 min read",
    author: "Naari Sutra Team",
    excerpt:
      "Good nutrition isn't a rigid plan — it's one that bends around your real, messy week.",
    image:
      "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    content: [
      { type: "p", text: "Most diet plans fail for the same reason: they're designed for an ideal week that never actually happens. They assume you'll cook every meal, have every ingredient, and never face a late meeting, a tired evening, or a kid who suddenly refuses vegetables. Real life is messier — and good nutrition has to survive that mess." },
      { type: "h2", text: "The myth of the perfect plan" },
      { type: "p", text: "A rigid seven-day meal plan looks great on paper. But the moment reality intervenes — you skip a day, you're out of an ingredient, plans change — the whole structure collapses, and with it, your motivation. The failure isn't yours. It's the plan's, for assuming a life no one actually lives." },
      { type: "quote", text: "The best plan is the one that bends without breaking." },
      { type: "h2", text: "Personalization that accounts for reality" },
      { type: "p", text: "Real personalization isn't just knowing you're vegetarian or that you dislike coriander. It's understanding the shape of your week: which nights you have time to cook, what's already in your kitchen, your budget, and how your energy rises and falls from Monday to Sunday." },
      { type: "list", items: [
        "Balances nutrition across the week, not just per meal.",
        "Adapts to what you actually have and what you actually like.",
        "Flexes around busy days instead of punishing them.",
      ]},
      { type: "h2", text: "How CookSorted approaches it" },
      { type: "p", text: "CookSorted is built on this philosophy. Instead of handing you a fixed plan to obey, it decides what to cook based on your pantry, preferences, nutrition goals, and the week ahead — then generates the grocery list to match. When the week changes, the plan changes with it." },
      { type: "p", text: "Nutrition shouldn't feel like a test you keep failing. Done right, it should feel like a quiet assistant who already knows your kitchen, your family, and your week — and simply tells you what's for dinner." },
    ],
  },
];
